import { useEffect, useMemo, useState } from "react";
import { useLanguage } from "../../../../../../stores/LanguageStore";
import {
  Button,
  Group,
  Indicator,
  LoadingOverlay,
  Modal,
  Select,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import { IconEdit, IconFilter } from "@tabler/icons-react";
import FilterContent from "../../../../../../components/Filter/FilterContent";
import { notifications } from "@mantine/notifications";
import PopOver from "../../../../../../components/View/PopOver";
import BackButton from "../../../../../../components/View/BackButton";
import { editCron, findCron } from "../../../../../../api/ConfigsApi";
import Jobs from "../../jobs/Jobs";
import { useConfigStore } from "../../../../../../stores/ConfigStore";
import CreatableContactListSelector from "../../../../../../components/Selectors/CreatableContactListSelector";
import PRMStatusSelector from "../../../../../../components/Selectors/PRMStatusSelector";

const EditPrmCron = ({ item, handleDone }) => {
  const lang = useLanguage((s) => s.language);
  const [opened, setOpened] = useState(false);
  const [finding, setFinding] = useState(false);
  const [loading, setLoading] = useState(false);
  const [list, setList] = useState(null);
  const [itemProfile, setItemProfile] = useState(item);
  const [filter, setFilter] = useState(null);
  const [status, setStatus] = useState(null);

  const datafields = useConfigStore((s) => s.datafields);

  const fields = useMemo(() => {
    return [
      {
        label: lang.forms.fields,
        options: [
          { field: "id", id: "id", option_name: "ID" },
          ...datafields.map((x) => ({ ...x, option_name: x.name })),
        ],
      },
    ];
  }, [datafields]);

  const handleOpen = () => {
    setOpened(true);
    setFilter(null);
  };
  const handleClose = () => {
    setOpened(false);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setLoading(true);
    let formData = new FormData(e.target);
    let data = Object.fromEntries(formData);

    const custom_status = parseInt(data.status);

    const args = {
      type: "contactlists_copy_contact_list_to_my_prm",
      from_contact_list_id: list.id,
      contacts_selection: {
        filter: filter || { mode: "or", values: [] },
        contact_ids: [],
        excluded_contact_ids: [],
      },
      custom_status: custom_status || undefined,
      status: custom_status ? "opener" : data.status,
    };

    editCron({
      id: item.id,
      name: data?.name,
      frequency: data?.frequency,
      args,
    })
      .then((response) => {
        notifications.show({
          message: lang.contact_lists.cron.edited,
          color: "tertiary.8",
        });
        handleClose();
        handleDone();
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    if (opened) {
      setFinding(true);
      findCron(item.id)
        .then((response) => {
          let itemArgs = response.data.cron_profile.arguments;
          setItemProfile(response.data.cron_profile);
          setFilter(itemArgs.contacts_selection.filter);
          setList({
            id: itemArgs.from_contact_list_id,
          });
          setStatus(itemArgs?.custom_status || itemArgs?.status);
        })
        .finally(() => setFinding(false));
    }
  }, [item.id, opened]);

  return (
    <>
      <Button
        onClick={handleOpen}
        leftSection={<IconEdit size={18} />}
        variant="light"
        mr={"auto"}
      >
        {lang.contact_lists.cron.view}
      </Button>

      <Modal
        opened={opened}
        title={lang.contact_lists.cron.edit_title + item.name}
        onClose={handleClose}
        size={"50%"}
      >
        <LoadingOverlay visible={finding} />
        <Stack
          component={"form"}
          onSubmit={onSubmit}
          key={itemProfile?.arguments ? "a" : "b"}
        >
          <Group wrap="nowrap">
            <TextInput
              name="name"
              defaultValue={itemProfile.name}
              label={lang.prm.nurturing.create_name}
              placeholder={lang.prm.nurturing.create_name}
              required
              w={"100%"}
              autoFocus
              data-autofocus
              size="md"
            />

            <Select
              name="frequency"
              defaultValue={itemProfile.frequency}
              label={lang.sync_zapier.event_label}
              placeholder={lang.sync_zapier.event_label}
              required
              data={Object.keys(lang.sync_zapier.frequency).map((x) => ({
                value: x,
                label: lang.sync_zapier.frequency[x],
              }))}
              size="md"
            />
          </Group>

          <CreatableContactListSelector
            value={list}
            setValue={setList}
            label={lang.contact_lists.cron.list_source}
            placeholder={lang.contact_lists.create.dialog.desc}
            w={"100%"}
            size="md"
            required
            disableCreate
            noDesc
          />

          <PRMStatusSelector
            label={"Status"}
            required
            withinPortal={true}
            value={status?.toString()}
            onChange={setStatus}
            name={"status"}
            id={"status"}
            size={"md"}
          />

          <Text mb={-8}>{lang.contact_lists.cron.filter_text}</Text>
          <PopOver
            opener={(openFunction, ref) => {
              return (
                <Button
                  ref={ref}
                  variant="default"
                  onClick={openFunction}
                  id={"nurturing-filter-" + "creation"}
                  size="md"
                >
                  <Indicator
                    color="red"
                    offset={2}
                    disabled={filter?.items?.length === 0 || !filter}
                    label={filter?.items?.length}
                    size={12}
                  >
                    <IconFilter size={18} />
                  </Indicator>
                  <span style={{ marginLeft: 6 }}>
                    {lang.table.toolbar.filter.main_button_label} *
                  </span>
                </Button>
              );
            }}
            popoverChildren={(closePopover) => (
              <FilterContent
                fields={fields}
                id={"cron-create"}
                isClient={false}
                pageName={"contact_lists_contacts"}
                maxHeight={350}
                mainFilter={filter}
                setMainFilter={(v) => {
                  closePopover();
                  setFilter(v);
                }}
                defaultFiltersClose
                getFieldOptionName={(f) => f.option_name}
              />
            )}
            elevation={3}
          />

          {itemProfile?.state_details?.length > 0 && (
            <Group>
              <button
                style={{ display: "none" }}
                id="cron-reload"
                type="button"
                onClick={() => {
                  findCron(item.id).then((response) => {
                    setItemProfile(response.data.cron_profile);
                  });
                }}
              >
                Reload cron
              </button>
              <Text>{lang.contact_lists.cron.processes}</Text>
              <Jobs isCron={true} row={itemProfile} onDiff={() => {}} />
            </Group>
          )}

          <Group justify="end" mt={"lg"}>
            <BackButton onClick={handleClose} size="md" />
            <Button
              // disabled={
              //   type === "prm" ? !filter || filter?.values?.length === 0 : false
              // }
              leftSection={<IconEdit size={18} />}
              loading={loading}
              type="submit"
              size="md"
            >
              {lang.contact_lists.cron.edit}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
};

export default EditPrmCron;
